import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button } from 'react-bootstrap';
import Select from 'react-select';
import CurrencyInput from 'react-currency-input-field';
import { Entities, Cptys } from '../../data/StaticData';

const TradeEntry = ({ trade = {}, isNew, user, onTradeUpdate, onValidationUpdate, viewOnly = false }) => {

  const tradesApiUrl = process.env.REACT_APP_TRADES_API;

  const [data, setData] = useState(trade);
  const [view, setView] = useState(!!trade);
  const [parties, setParties] = useState({});
  const [instruments, setInstruments] = useState({});
  const [instrumentOptions, setInstrumentOptions] = useState([]);
  const [terms, setTerms] = useState({});
  const [currencies, setCurrencies] = useState({});
  const [fx, setFx] = useState({});
  const [fxDates, setFxDates] = useState({});
  const [loading, setLoading] = useState(true);

  const DEFAULT_TERMS = {
    ON: { termId: 'ON', start: 0, days: 1 },
    '1W': { termId: '1W', start: 0, days: 7 },
    '2W': { termId: '2W', start: 0, days: 14 },
    '1M': { termId: '1M', start: 0, days: 30 },
    '3M': { termId: '3M', start: 0, days: 90 },
    '6M': { termId: '6M', start: 0, days: 180 },
    '1Y': { termId: '1Y', start: 0, days: 360 }
  };

  const DEFAULT_CURRENCIES = {
    USD: { currencyId: 'USD', name: 'USD' },
    GBP: { currencyId: 'GBP', name: 'GBP' },
    EUR: { currencyId: 'EUR', name: 'EUR' },
    BHD: { currencyId: 'BHD', name: 'BHD' },
    JPY: { currencyId: 'JPY', name: 'JPY' },
    SAR: { currencyId: 'SAR', name: 'SAR' },
    QAR: { currencyId: 'QAR', name: 'QAR' },
    AED: { currencyId: 'AED', name: 'AED' },
    OMR: { currencyId: 'OMR', name: 'OMR' },
    KWD: { currencyId: 'KWD', name: 'KWD' }
  };

  const DEFAULT_FX = {
    USDUSD: 1,
    USDGBP: 0.77,
    USDEUR: 0.93,
    USDBHD: 0.38,
    USDJPY: 151.81
  };

  const DEFAULT_PARTIES = {
    ...Object.entries(Entities || {}).reduce((acc, [key, value]) => {
      acc[key] = { name: value?.name || key };
      return acc;
    }, {}),
    ...Object.entries(Cptys || {}).reduce((acc, [key, value]) => {
      acc[key] = { name: value?.name || key };
      return acc;
    }, {})
  };

  // Custom function to calculate days between two dates
  const calculateDaysBetweenDates = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = endDate - startDate;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const normalizeIsin = (value) => {
    if (typeof value !== 'string') return value;
    return value.startsWith('ISIN#') ? value.replace('ISIN#', '') : value;
  };

  const getInstrumentForIsin = (value) => {
    if (!value) return undefined;
    const normalized = normalizeIsin(value);
    return instruments[value] || instruments[normalized] || instruments[`ISIN#${normalized}`];
  };

  const getIsinFromData = (tradeData) => {
    if (!tradeData) return undefined;
    return tradeData.isin || tradeData.ISIN || tradeData.bondIsin || tradeData.bondISIN;
  };

  const getBondCcyFromData = (tradeData) => {
    if (!tradeData) return undefined;
    return tradeData.bondCcy || tradeData.bondCurrency || tradeData.bond_ccy || tradeData.ccy;
  };

  const getPartyLabel = (partyId) => {
    if (!partyId) return '';
    return parties[partyId]?.name || partyId;
  };

  const getCcyLabel = (ccyId) => {
    if (!ccyId) return '';
    return currencies[ccyId]?.name || ccyId;
  };

  const displayIsin = getIsinFromData(data) || '';
  const selectedInstrument = displayIsin ? getInstrumentForIsin(displayIsin) : null;
  const selectedInstrumentOption =
    instrumentOptions.find((option) => option.value === displayIsin) ||
    (displayIsin ? { value: displayIsin, label: displayIsin } : null);
  const partyOptions = Object.entries(parties)
    .map(([partyId, party]) => ({
      value: partyId,
      label: `${party?.name || partyId} (${partyId})`,
      searchText: `${party?.name || ''} ${partyId}`.trim()
    }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  const selectedEntityOption = data.entity
    ? partyOptions.find((option) => option.value === data.entity) || { value: data.entity, label: data.entity }
    : null;
  const selectedCptyOption = data.cpty
    ? partyOptions.find((option) => option.value === data.cpty) || { value: data.cpty, label: data.cpty }
    : null;

  // Fetch reference data from APIs
  const loadReferenceData = async () => {
    try {
      setLoading(true);

      // Fetch all reference data in parallel (no auth required for read-only data)
      const [instrumentsRes, termsRes, partiesRes, cptsRes, ccysRes, fxRes] = await Promise.all([
        fetch(`${tradesApiUrl}/instruments`),
        fetch(`${tradesApiUrl}/terms`),
        fetch(`${tradesApiUrl}/parties`),
        fetch(`${tradesApiUrl}/counterparties`),
        fetch(`${tradesApiUrl}/currencies`),
        fetch(`${tradesApiUrl}/fxrates`)
      ]);

      const [instrumentsData, termsData, partiesData, cptsData, ccysData, fxData] = await Promise.all([
        instrumentsRes.json(),
        termsRes.json(),
        partiesRes.json(),
        cptsRes.json(),
        ccysRes.json(),
        fxRes.json()
      ]);

      // Convert instruments to keyed object
      const instrumentsMap = {};
      const instrumentOptionsMap = {};
      instrumentsData.items?.forEach(item => {
        const normalizedIsin = normalizeIsin(item.isin || item.id);
        if (item.isin) {
          instrumentsMap[item.isin] = item;
          instrumentsMap[`ISIN#${item.isin}`] = item;
        }
        if (item.id) {
          instrumentsMap[item.id] = item;
        }
        if (normalizedIsin) {
          instrumentsMap[normalizedIsin] = item;
          if (!instrumentOptionsMap[normalizedIsin]) {
            instrumentOptionsMap[normalizedIsin] = {
              value: normalizedIsin,
              label: `${item.bondName || normalizedIsin} (${normalizedIsin})`,
              searchText: `${item.bondName || ''} ${normalizedIsin} ${item.ccy || ''} ${item.coupon || ''}`.trim(),
            };
          }
        }
      });
      setInstruments(instrumentsMap);
      const sortedOptions = Object.values(instrumentOptionsMap).sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
      );
      setInstrumentOptions(sortedOptions);

      // Convert terms to keyed object
      const termsMap = {};
      termsData.items?.forEach(item => {
        termsMap[item.termId] = item;
      });
      setTerms(Object.keys(termsMap).length > 0 ? termsMap : DEFAULT_TERMS);

      // Convert currencies to keyed object
      const currenciesMap = {};
      ccysData.items?.forEach(item => {
        currenciesMap[item.currencyId] = item;
      });
      setCurrencies(Object.keys(currenciesMap).length > 0 ? currenciesMap : DEFAULT_CURRENCIES);

      // Convert FX rates to keyed object
      const fxMap = {};
      const fxDateMap = {};
      fxData.items?.forEach(item => {
        fxMap[item.pair] = item.rate;
        fxDateMap[item.pair] = item.date;
      });
      setFx(Object.keys(fxMap).length > 0 ? fxMap : DEFAULT_FX);
      setFxDates(Object.keys(fxDateMap).length > 0 ? fxDateMap : {});

      // Combine parties and counterparties
      const allParties = {};
      partiesData.items?.forEach(item => {
        allParties[item.partyId] = { name: item.name };
      });
      cptsData.items?.forEach(item => {
        allParties[item.counterpartyId] = { name: item.name };
      });
      setParties(Object.keys(allParties).length > 0 ? allParties : DEFAULT_PARTIES);

      setLoading(false);
    } catch (error) {
      console.error('Error loading reference data:', error);
      setLoading(false);
    }
  };
  
  // Update days whenever start or end date changes
  useEffect(() => {
    if (data.start && data.end) {
      const days = calculateDaysBetweenDates(data.start, data.end);
      setData((prevData) => ({
        ...prevData,
        days,
      }));
    }
  }, [data.start, data.end]);
  
  // Load reference data on component mount
  useEffect(() => {
    if (tradesApiUrl) {
      loadReferenceData();
    }
  }, [tradesApiUrl]);

  const validateFields = () => {
    const isValid = requiredFields.every((field) => data[field]);
    onValidationUpdate(isValid); // Update parent component's validation state
  };

  useEffect(()=>{
    onTradeUpdate(data);
    validateFields();
  },[data])
  
  // Retrieve values from `data` with a fallback for missing or undefined values
  const getFieldValue = (key, defaultValue = '') => {
    return data[key] !== undefined && data[key] !== null ? data[key] : defaultValue;
  };

  

  
  useEffect(() => {
    // Set the default entity in data based on user.party when available
    if (!viewOnly && user.party) {
      console.log('setting default party');
      setData((prevData) => ({
        ...prevData,
        entity: user.party,  // Store user.party as entity in data
      }));
    }
  }, [user.party, parties]);
  

  // Update `data` in state with the changed field
  const onFieldChange = (e) => {
    if (viewOnly) return

    const { name, value } = e.target;
    setData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Custom handler for bond selection to retrieve and set ISIN and currency
  const handleBondChange = (bondKey) => {
    if (viewOnly) return
    const selectedInstrument = getInstrumentForIsin(bondKey);
    const normalizedIsin = normalizeIsin(bondKey);

    if (selectedInstrument) {
      setData((prevData) => ({
        ...prevData,
        bondName: selectedInstrument.bondName,
        isin: normalizedIsin || bondKey,
        bondCcy: selectedInstrument.ccy,
      }));
    } else {
      setData((prevData) => ({
        ...prevData,
        bondName: '',
        isin: '',
        bondCcy: '',
      }));
    }
  };

  useEffect(() => {
    const rawIsin = getIsinFromData(data);
    if (!rawIsin) return;
    const instrument = getInstrumentForIsin(rawIsin);
    if (!instrument) {
      const fallbackCcy = getBondCcyFromData(data);
      if (!fallbackCcy || fallbackCcy === 'N/A') return;
      setData((prevData) =>
        prevData.bondCcy === fallbackCcy
          ? prevData
          : { ...prevData, bondCcy: fallbackCcy }
      );
      return;
    }
    const normalizedIsin = normalizeIsin(rawIsin);
    setData((prevData) => {
      const updates = {};
      if (instrument.ccy && prevData.bondCcy !== instrument.ccy) {
        updates.bondCcy = instrument.ccy;
      }
      if (instrument.bondName && prevData.bondName !== instrument.bondName) {
        updates.bondName = instrument.bondName;
      }
      if (normalizedIsin && prevData.isin !== normalizedIsin) {
        updates.isin = normalizedIsin;
      }
      if (prevData.bondCcy === 'N/A') {
        updates.bondCcy = instrument.ccy;
      }
      return Object.keys(updates).length > 0 ? { ...prevData, ...updates } : prevData;
    });
  }, [data.isin, data.ISIN, data.bondIsin, data.bondISIN, instruments]);


  // Custom handler for term selection to set start date, end date, and days
  const handleTermChange = (e) => {
    if (viewOnly) return

    const termKey = e.target.value;
    const selectedTerm = terms[termKey];

    if (selectedTerm) {
      // Calculate start date as today + term.start
      const startDate = new Date();
      startDate.setDate(startDate.getDate() + selectedTerm.start);
      
      const endDate = new Date();
      endDate.setDate(startDate.getDate() + selectedTerm.days);

      setData((prevData) => ({
        ...prevData,
        term: termKey,
        start: startDate.toISOString().split('T')[0],  // Format date to 'YYYY-MM-DD'
        end: endDate.toISOString().split('T')[0],
        days: selectedTerm.days,
      }));
    } else {
      setData((prevData) => ({
        ...prevData,
        term: '',
        start: '',
        end: '',
        days: '',
      }));
    }
  };

  // Function to set tradeType and update button appearance
  const handleTradeTypeChange = (type) => {
    if (viewOnly) return
    
    setData((prevData) => ({
      ...prevData,
      tradeType: type,
    }));
  };

  const recalculateStartCash = () => {
    const { nominal, aip, fx, haircut } = data;
    const effectiveAip = aip ?? 100; // Default to 100 if aip is missing
    const effectiveFx = fx ?? 1
    if (nominal) {
      const startCash = (nominal * (effectiveAip / 100)) * (haircut/100) *effectiveFx ; // Updated calculation
      setData((prevData) => ({
        ...prevData,
        startCash,
      }));
    }
  };
  
  useEffect(() => {
    recalculateStartCash();
  }, [data.nominal, data.aip, data.fx, data.haircut]);
  


  const recalculateInterest = () => {
    const { startCash, repoRate, days } = data;
    if (startCash && repoRate) {
      const interest = startCash * (repoRate / 100) * (days/360);

      setData((prevData) => ({
        ...prevData,
        interest,
      }));
    }
  };
  
  const recalculateEndCash = () => {
    const { startCash, interest } = data;
    if (startCash && interest) {
      const endCash = startCash + interest;
      setData((prevData) => ({
        ...prevData,
        endCash,
      }));
    }
  };
  
  useEffect(() => {
    recalculateInterest();
  }, [data.startCash, data.repoRate, data.start, data.end]);
  
  useEffect(() => {
    recalculateEndCash();
  }, [data.startCash, data.interest]);

  useEffect(() => {
    if (data.bondCcy && data.ccy && fx) {
      const fxpair = `${data.bondCcy}${data.ccy}`; // Concatenate bondCcy and ccy to form fxpair
      const fxrate = fx[fxpair];
      const fxdate = fxDates[fxpair];
      console.log('FX Lookup:', { bondCcy: data.bondCcy, ccy: data.ccy, fxpair, fxrate, fxDate: fxdate, fxData: fx });
      
      setData((prevData) => ({
        ...prevData,
        fxpair: fxpair,
        fx: fxrate || 1,
        fxDate: fxdate
      }));
    }
  }, [data.bondCcy, data.ccy, fx, fxDates]);
  
  const recalculateAIP = () => {
    const instrument = getInstrumentForIsin(getIsinFromData(data));
    if (!instrument) return 0;
    const lcd = getLastCouponDate(instrument.endDate, data.start);
    const noDays = getDaysBetween(lcd, data.start);
    const ai = noDays/360*instrument.coupon
    const aip = Number(data.cleanPrice) + Number(ai);

    console.log('aip = ', aip)
    setData((prevData) => ({
      ...prevData,
      aip,
    }));
  }

  function getDaysBetween(date1Str, date2Str) {
    const date1 = new Date(date1Str);
    const date2 = new Date(date2Str);

    // Extract year, month, and day
    const year1 = date1.getFullYear();
    const month1 = date1.getMonth();
    const day1 = date1.getDate();

    const year2 = date2.getFullYear();
    const month2 = date2.getMonth();
    const day2 = date2.getDate();

    // Calculate year and month differences
    const yearDiff = year2 - year1;
    const monthDiff = month2 - month1;
    
    // Total months difference
    const totalMonths = (yearDiff * 12) + monthDiff;

    // Convert months to days (each month = 30 days)
    const monthDays = totalMonths * 30;

    // Calculate day difference
    const dayDiff = day2 - day1;

    // Total days
    return monthDays + dayDiff;
}





  useEffect(()=>{
    if (instruments && data.isin && data.start && data.cleanPrice) recalculateAIP()
  },[data.isin, data.start, data.cleanPrice])
  
  /**
 * Calculates the last coupon date for a semi-annual bond
 * @param {Date} maturityDate - The maturity date of the bond
 * @param {Date} evaluationDate - The date from which to calculate (usually today)
 * @returns {Date} The last coupon date before or on the evaluation date
 */
function getLastCouponDate(maturityDate, evaluationDate) {
  // Convert input dates to ensure we're working with Date objects
  maturityDate = new Date(maturityDate);
  evaluationDate = new Date(evaluationDate);
  
  // Validate inputs
  if (!(maturityDate instanceof Date && !isNaN(maturityDate))) {
      throw new Error('Invalid maturity date');
  }
  if (!(evaluationDate instanceof Date && !isNaN(evaluationDate))) {
      throw new Error('Invalid evaluation date');
  }
  
  // Start from maturity date and work backwards
  let couponDate = new Date(maturityDate);
  
  // Keep track of previous date for comparison
  let previousDate = new Date(couponDate);
  
  // Work backwards in 6-month intervals until we find the last coupon date
  while (couponDate > evaluationDate) {
      previousDate = new Date(couponDate);
      
      // Move back 6 months
      if (couponDate.getMonth() < 6) {
          couponDate.setMonth(couponDate.getMonth() + 6);
          couponDate.setFullYear(couponDate.getFullYear() - 1);
      } else {
          couponDate.setMonth(couponDate.getMonth() - 6);
      }
  }
  
  return couponDate;
}







const handleNominalChange = (value) => {
  setData((prevData) => ({
    ...prevData,
    nominal: value ? parseFloat(value) : 0,
  }));
};

const getStartCash = ()=>{
  console.log('startCash = ', data.startCash)
  if (data.startCash) return new Number(data.startCash).toFixed(2)
    return 0
}

  const requiredFields = ['ccy', 'cleanPrice', 'nominal', 'repoRate', 'isin', 'term', 'start', 'end', 'entity', 'cpty', 'haircut', 'aip', 'tradeType'];

  return (
    <Container fluid style={{ padding: '20px', backgroundColor: 'white', color: 'black' }}>
      <Row className="mb-3">
        <Col md={12}>
          <Form.Group>
            <Form.Label>Sukuk</Form.Label>
            <Select
              inputId="isin"
              name="isin"
              placeholder="Select Sukuk"
              value={selectedInstrumentOption}
              options={instrumentOptions}
              isDisabled={viewOnly}
              isClearable
              onChange={(option) => handleBondChange(option?.value || '')}
              filterOption={(option, inputValue) =>
                option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
                (option.data?.searchText || option.searchText || '').toLowerCase().includes(inputValue.toLowerCase())
              }
              styles={{
                control: (base) => ({
                  ...base,
                  backgroundColor: 'yellow',
                  borderColor: '#ced4da',
                  minHeight: '38px'
                }),
                singleValue: (base) => ({
                  ...base,
                  color: 'black'
                }),
                menu: (base) => ({
                  ...base,
                  zIndex: 5
                })
              }}
            />
            {selectedInstrument && (
              <small className="text-muted d-block mt-1">
                {selectedInstrument.bondName || 'Bond'} · ISIN {selectedInstrument.isin || displayIsin} · {selectedInstrument.ccy || getFieldValue('bondCcy') || 'CCY'} · Coupon {selectedInstrument.coupon ?? '-'}
              </small>
            )}
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={2}>
          <Form.Group>
            <Form.Label>Trade ID</Form.Label>
            <Form.Control readOnly value={getFieldValue('id')} />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>ISIN</Form.Label>
            <Form.Control readOnly value={displayIsin} style={{ backgroundColor: '#e9ecef' }} />
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group>
            <Form.Label>UL Ccy</Form.Label>
            <Form.Control readOnly value={getFieldValue('bondCcy')} style={{ backgroundColor: '#e9ecef' }} />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Clean Price</Form.Label>
            <Form.Control
              type="number"
              name="cleanPrice"
              style={{ backgroundColor: 'lightblue' }}
              value={getFieldValue('cleanPrice')}
              onChange={onFieldChange}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
      <Col md={3}>
          <Form.Group>
            <Form.Label>Entity</Form.Label>
            <Form.Control
              readOnly
              value={getPartyLabel(getFieldValue('entity'))}
              style={{ backgroundColor: '#e9ecef' }}
            />
            {getFieldValue('entity') && parties[getFieldValue('entity')]?.organization && (
              <small className="text-muted d-block mt-1">
                {parties[getFieldValue('entity')].organization}
              </small>
            )}
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>C/P</Form.Label>
            <Select
              inputId="cpty"
              name="cpty"
              placeholder="Select counterparty"
              value={selectedCptyOption}
              options={partyOptions.filter((option) => option.value !== user.party)}
              isDisabled={viewOnly}
              isClearable
              onChange={(option) =>
                setData((prevData) => ({
                  ...prevData,
                  cpty: option?.value || ''
                }))
              }
              filterOption={(option, inputValue) =>
                option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
                (option.data?.searchText || option.searchText || '').toLowerCase().includes(inputValue.toLowerCase())
              }
              styles={{
                control: (base) => ({
                  ...base,
                  backgroundColor: 'yellow',
                  borderColor: '#ced4da',
                  minHeight: '38px'
                }),
                singleValue: (base) => ({
                  ...base,
                  color: 'black'
                }),
                menu: (base) => ({
                  ...base,
                  zIndex: 5
                })
              }}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Repo Rate (%)</Form.Label>
            <Form.Control
              type="number"
              name="repoRate"
              style={{ backgroundColor: 'lightblue' }}
              value={getFieldValue('repoRate')}
              onChange={onFieldChange}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Term</Form.Label>
            <Form.Control
              as="select"
              name="term"
              style={{ backgroundColor: 'yellow' }}
              onChange={handleTermChange}
              value={getFieldValue('term')}
            >
              <option value="">Select Term</option>
              {Object.keys(terms).map((key) => (
                <option key={key} value={key}>
                  {key}
                </option>
              ))}
            </Form.Control>
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={3}>
          <Form.Group>
            <Form.Label>Start Date</Form.Label>
            <Form.Control
              type="date"
              name="start"
              value={getFieldValue('start')}
              onChange={onFieldChange}
              
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>End Date</Form.Label>
            <Form.Control
              type="date"
              name="end"
              value={getFieldValue('end')}
              onChange={onFieldChange}
              
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Days</Form.Label>
            <Form.Control readOnly value={getFieldValue('days')} />
          </Form.Group>
        </Col>
        <Col md={3}>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={3}>
          <Form.Group>
            <Form.Label>Nominal</Form.Label>
            {/** 
            <Form.Control
              type="number"
              name="nominal"
              style={{ backgroundColor: 'lightblue' }}
              value={getFieldValue('nominal')}
              onChange={onFieldChange}
            />*/}

            <CurrencyInput
              name="nominal"
              style={{
                backgroundColor: 'lightblue',
                width: '100%',
                padding: '8px',
                border: '1px solid #ced4da',
                borderRadius: '4px',
              }}
              decimalsLimit={2}
              value={data.nominal}
              onValueChange={handleNominalChange}
              allowNegativeValue={false}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Profit</Form.Label>
            <Form.Control
              type="number"
              name="aip"
              style={{ backgroundColor: 'orange' }}
              value={getFieldValue('aip')}
              onChange={onFieldChange}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Haircut (%)</Form.Label>
            <Form.Control
              type="number"
              name="haircut"
              style={{ backgroundColor: 'lightblue' }}
              value={getFieldValue('haircut')}
              onChange={onFieldChange}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Start Cash</Form.Label>
            {/** 
            <Form.Control
              type="number"
              name="startCash"
              style={{ backgroundColor: 'orange' }}
              value={getFieldValue('startCash')}
              onChange={onFieldChange}
            />*/}

            <CurrencyInput
              name="startCash"
              style={{
                backgroundColor: 'orange',
                width: '100%',
                padding: '8px',
                border: '1px solid #ced4da',
                borderRadius: '4px',
              }}
              decimalScale={2}
              value={getStartCash()}
              readOnly
              disabled
            />

          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Cash Ccy</Form.Label>
            <Form.Control
              as="select"
              name="ccy"
              style={{ backgroundColor: 'yellow' }}
              value={getFieldValue('ccy')}
              onChange={onFieldChange}
            >
              <option value="">Select Currency</option>
              {getFieldValue('ccy') && !currencies[getFieldValue('ccy')] && (
                <option value={getFieldValue('ccy')}>{getCcyLabel(getFieldValue('ccy'))}</option>
              )}
              {Object.keys(currencies).map((key) => (
                <option key={key} value={key}>
                  {getCcyLabel(key)}
                </option>
              ))}
            </Form.Control>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>FX Rate</Form.Label>
            <Form.Control
              type="number"
              name="fx"
              style={{ backgroundColor: '#e9ecef' }}
              value={getFieldValue('fx')}
              readOnly
            />
          </Form.Group>
        </Col>
        <Col md={3} className="d-flex align-items-end">
          {fxDates[data.fxpair] && (
            <div style={{ paddingBottom: '7px' }}>
              <span style={{ color: '#6c757d', fontSize: '0.875rem' }}>
                Rate date: <strong style={{ color: '#495057' }}>{fxDates[data.fxpair]}</strong>
              </span>
            </div>
          )}
        </Col>
      </Row>
      <Row className='mb-4'>

      </Row>
      <Row>
        <Col md={6} className="d-grid gap-2">
          <Button
            variant={data.tradeType === 'buy' ? 'success' : 'outline-success'}
            onClick={() => handleTradeTypeChange('buy')}
          >
            Bid / Buy
          </Button>
        </Col>
        <Col md={6} className="d-grid gap-2">
          <Button
            variant={data.tradeType === 'sell' ? 'danger' : 'outline-danger'}
            onClick={() => handleTradeTypeChange('sell')}
          >
            Offer / Sell
          </Button>
        </Col>
      </Row>
    </Container>
  );
};

export default TradeEntry;
